import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import {
  Box, Button, TextField, Typography, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, MenuItem,
  IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, Chip
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { loginUser, getCommandes, updateCommandeStatus } from '../utils/api';
import dayjs from 'dayjs';
import './DashboardCommandes.css';

const sha256Hash = async (text) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const formatPickup = (dt) => {
  const m = dayjs(dt);
  const now = dayjs();
  if (m.isSame(now, 'day')) return `Aujourd’hui, ${m.format('HH:mm')}`;
  if (m.isSame(now.add(1, 'day'), 'day')) return `Demain, ${m.format('HH:mm')}`;
  return m.format('DD/MM/YYYY HH:mm');
};

const getMinutesToPickup = (dt) => {
  const now = dayjs();
  const pickup = dayjs(dt);
  const diff = pickup.diff(now, 'minute');
  return diff;
};

const statusStyles = {
  'Demande': { label: 'Demande', color: 'warning', variant: 'outlined' },
  'Validée': { label: 'Validée', color: 'success', variant: 'outlined' },
  'Terminée': { label: 'Terminée', color: 'default', variant: 'outlined' },
  'Annulée': { label: 'Annulée', color: 'error', variant: 'outlined' }
};

const DashboardCommandes = () => {
  const [loggedUser, setLoggedUser] = useState(sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')) : null);
  const [loginInfo, setLoginInfo] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [commandes, setCommandes] = useState([]);
  const [showTerminees, setShowTerminees] = useState(false);
  const [dialogCmd, setDialogCmd] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [dialogChangeTime, setDialogChangeTime] = useState({ open: false, cmdId: null });
  const [newPickupTime, setNewPickupTime] = useState('');

  useEffect(() => {
    const loadCarte = async () => {
      try {
        const res = await fetch(`/assets/json/carte.json?v=${Date.now()}`);
        const data = await res.json();
  
        const itemsMap = {};
  
        if (data.categories) {
          Object.values(data.categories).forEach(liste => {
            if (Array.isArray(liste)) {
              liste.forEach(item => {
                if (!item) return;
                if (item.ref) {
                  itemsMap[item.ref] = item; // ⚡ Indexation par ref
                }
              });
            }
          });
        }
  
       //console.log("✅ itemsByRef keys :", Object.keys(itemsMap));
      } catch (err) {
        console.error("Erreur chargement carte.json", err);
      }
    };
  
    loadCarte();
  }, []);
  
  const loadCommandes = async () => {
    try {
      const res = await getCommandes();
     // console.log('Commandes chargées :', res.data);
      setCommandes(res.data);
    } catch (e) {
      console.error(e);
      setError('Erreur de chargement des commandes');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const hashedPassword = await sha256Hash(loginInfo.password);
   // console.log('Email:', loginInfo.email, 'Hash:', hashedPassword);

    try {
      const user = await loginUser(loginInfo.email, loginInfo.password);
      setLoggedUser(user);
      sessionStorage.setItem('user', JSON.stringify(user));
      loadCommandes();
    } catch (e) {
      console.error(e);
      setError(e.message);
    }
  };

  useEffect(() => {
    if (!loggedUser) return;
    loadCommandes();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadCommandes();
    }, 15000);
    return () => clearInterval(interval);
  }, [loggedUser]);

  const handleStatusChange = async (id, newStatus) => {
    if (newStatus === 'Validée avec changement d\'horaire') {
      const commande = commandes.find(cmd => cmd.id === id); // Trouver la commande correspondante
      if (commande) {
        setNewPickupTime(dayjs(commande.pickup_datetime).format('YYYY-MM-DDTHH:mm')); // Initialiser avec la valeur actuelle
      }
      setDialogChangeTime({ open: true, cmdId: id });
      return;
    }
  
    try {
      const updates = { status: newStatus }; // Assurez-vous que `status` est envoyé comme un objet
    //  console.log('Données envoyées pour un changement de statut simple:', updates);
  
      await updateCommandeStatus(id, updates);
      loadCommandes();
    } catch (e) {
      console.error(e);
      setError('Impossible de mettre à jour la commande');
    }
  };

  const handleConfirmChangeTime = async () => {
    try {
      const updates = {
        status: 'Validée', // Met à jour le statut
        pickup_datetime: newPickupTime // Met à jour la date et l'heure de retrait
      };

     // console.log('Données envoyées à updateCommandeStatus:', updates); // Ajout d'un log pour débogage

      await updateCommandeStatus(dialogChangeTime.cmdId, updates);

      // Réinitialisation de l'état de la modale et rechargement des commandes
      setDialogChangeTime({ open: false, cmdId: null });
      setNewPickupTime('');
      loadCommandes();
    } catch (e) {
      console.error(e);
      setError('Impossible de mettre à jour l\'horaire');
    }
  };

  const isRetraitImminent = (pickup_datetime) => {
    const now = dayjs();
    const pickup = dayjs(pickup_datetime);
    const diff = pickup.diff(now, 'minute');
    return diff >= 0 && diff <= 15;
  };

  const commandesActives = commandes.filter(cmd => cmd.status !== 'Terminée' && cmd.status !== 'Annulée');
  const commandesTerminees = commandes.filter(cmd => cmd.status === 'Terminée' || cmd.status === 'Annulée');

  if (!loggedUser) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 5 }}>
        <Typography variant="h5" gutterBottom>Connexion restaurateur</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={loginInfo.email}
            onChange={(e) => setLoginInfo({ ...loginInfo, email: e.target.value })}
            fullWidth margin="normal" required
          />
          <TextField
            label="Mot de passe"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={loginInfo.password}
            onChange={(e) => setLoginInfo({ ...loginInfo, password: e.target.value })}
            fullWidth margin="normal" required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
            Se connecter
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="dashboard-container">
      <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '1px solid #eee', pb: 2 }}>
        <Button variant="contained" size="small" disabled>Commandes</Button>
        <Button component={Link} to="/dashboard/plats" variant="outlined" size="small">Modifier les plats</Button>
        <Button component={Link} to="/dashboard/menus" variant="outlined" size="small">Modifier les menus</Button>
        <Button component={Link} to="/dashboard/categories" variant="outlined" size="small">Catégories</Button>
        <Button component={Link} to="/dashboard/reglages" variant="outlined" size="small">Fermeture / Vacances</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="error" onClick={() => { sessionStorage.removeItem('user'); setLoggedUser(null); }}>
          Déconnexion
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom>Dashboard des commandes</Typography>
      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="h6" sx={{ mt: 3 }}>Commandes en cours</Typography>
      <Table className="commandes-table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Retrait</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {commandesActives.map(cmd => (
            <TableRow
              key={cmd.id}
              className={
                cmd.status === 'Demande'
                  ? 'demande-row'
                  : isRetraitImminent(cmd.pickup_datetime)
                    ? 'imminent-row'
                    : ''
              }
            >
              <TableCell>{cmd.id}</TableCell>
              <TableCell>{cmd.firstName} {cmd.lastName} ({cmd.phone})
                <br />{cmd.email}
                <br />Paiement : {cmd.payment_method}
                <br />Commentaire : {cmd.commentaire}
              </TableCell>
              <TableCell>
                {formatPickup(cmd.pickup_datetime)}
                {typeof cmd.pickup_datetime !== 'undefined' && (() => {
                  const min = getMinutesToPickup(cmd.pickup_datetime);
                  return (
                    <span
                      style={{
                        marginLeft: 8,
                        color: min < 0 ? 'red' : '#888',
                        fontWeight: min < 0 ? 'bold' : 'normal',
                        fontSize: '0.95em'
                      }}
                    >
                      ({min} min)
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell>{cmd.total} €</TableCell>
              <TableCell>
                <Chip
                  label={statusStyles[cmd.status]?.label || 'Inconnu'}
                  color={statusStyles[cmd.status]?.color || 'default'}
                  variant={statusStyles[cmd.status]?.variant || 'outlined'}
                />
              </TableCell>
              <TableCell>
                <TextField
                  select
                  value={cmd.status}
                  onChange={(e) => handleStatusChange(cmd.id, e.target.value)}
                  variant="standard"
                >
                  {['Demande', 'Validée', 'Terminée', 'Annulée'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
                <Button sx={{ ml: 1 }} size="small" onClick={() => setDialogCmd(cmd)}>Détails</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Button onClick={() => setShowTerminees(prev => !prev)} sx={{ mt: 3 }}>
        {showTerminees ? 'Masquer les Archives' : 'Voir les Archives'}
      </Button>

      {showTerminees && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Commandes terminées et annulées</Typography>
          <Table className="commandes-table terminees">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Retrait</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commandesTerminees.map(cmd => (
                <TableRow
                  key={cmd.id}
                  className={
                    cmd.status === 'Demande'
                      ? 'demande-row'
                      : isRetraitImminent(cmd.pickup_datetime)
                        ? 'imminent-row'
                        : ''
                  }
                >
                  <TableCell>{cmd.id}</TableCell>
                  <TableCell>{cmd.firstName} {cmd.lastName} ({cmd.phone})</TableCell>
                  <TableCell>
                    {formatPickup(cmd.pickup_datetime)}
                    {typeof cmd.pickup_datetime !== 'undefined' && (() => {
                      const min = getMinutesToPickup(cmd.pickup_datetime);
                      return (
                        <span
                          style={{
                            marginLeft: 8,
                            color: min < 0 ? 'red' : '#888',
                            fontWeight: min < 0 ? 'bold' : 'normal',
                            fontSize: '0.95em'
                          }}
                        >
                          ({min} min)
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{cmd.total} €</TableCell>
                  <TableCell>
                    <Chip
                      label={statusStyles[cmd.status]?.label || 'Inconnu'}
                      color={statusStyles[cmd.status]?.color || 'default'}
                      variant={statusStyles[cmd.status]?.variant || 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      value={cmd.status}
                      onChange={(e) => handleStatusChange(cmd.id, e.target.value)}
                      variant="standard"
                    >
                      {['Demande', 'Validée', 'Terminée', 'Annulée'].map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>
                    <Button sx={{ ml: 1 }} size="small" onClick={() => setDialogCmd(cmd)}>Détails</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Dialog open={!!dialogCmd} onClose={() => setDialogCmd(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Détails commande #{dialogCmd?.id}</DialogTitle>
        <DialogContent>
          {(() => {
            let items = [];

            try {
              items = Array.isArray(dialogCmd?.cartItems)
                ? dialogCmd.cartItems
                : JSON.parse(dialogCmd?.cartItems || '[]');
            } catch (e) {
              console.error('Erreur de parsing cartItems :', e);
              return <Typography color="error">Erreur de lecture du panier.</Typography>;
            }

            // Regrouper les plats identiques avec leurs quantités
            const platsMap = {};
            items.forEach((item) => {
              if (item.menuDetails) {
                if (item.menuDetails.selectedItems) {
                  // Pour les menus de type CartMenuPicker
                  item.menuDetails.selectedItems.forEach((menuItem) => {
                    platsMap[menuItem.nom] = (platsMap[menuItem.nom] || 0) + 1;
                  });
                } else {
                  // Pour les menus de type CartMenuItem
                  if (item.menuDetails.selectedEntrée) {
                    platsMap[item.menuDetails.selectedEntrée.nom] =
                      (platsMap[item.menuDetails.selectedEntrée.nom] || 0) + 1;
                  }
                  if (item.menuDetails.selectedPlat) {
                    platsMap[item.menuDetails.selectedPlat.nom] =
                      (platsMap[item.menuDetails.selectedPlat.nom] || 0) + 1;
                  }
                  if (item.menuDetails.selectedGarniture) {
                    platsMap[item.menuDetails.selectedGarniture.nom] =
                      (platsMap[item.menuDetails.selectedGarniture.nom] || 0) + 1;
                  }
                  if (item.menuDetails.selectedDessert) {
                    platsMap[item.menuDetails.selectedDessert.nom] =
                      (platsMap[item.menuDetails.selectedDessert.nom] || 0) + 1;
                  }
                }
              } else {
                // Pour les plats normaux
                platsMap[item.nom] = (platsMap[item.nom] || 0) + (item.quantity || 1);
              }
            });

            return (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>Détails des articles</Typography>
                {items.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {index + 1}. {item.ref ? `🛒 ${item.nom} (${item.ref})` : `🍱 ${item.nom}`}
                    </Typography>
                    <ul style={{ marginTop: 5, marginLeft: 16 }}>
                      {item.quantity && <li><strong>Quantité :</strong> {item.quantity}</li>}
                      {item.prix && <li><strong>Prix unitaire :</strong> {item.prix} €</li>}
                      {item['sous-categ'] && <li><strong>Sous-catégorie :</strong> {item['sous-categ']}</li>}
                      {item.description && <li><strong>Description :</strong> {item.description}</li>}
                      {item.menuDetails && (
                        <>
                          {item.menuDetails.selectedItems ? (
                            item.menuDetails.selectedItems.map((menuItem, idx) => (
                              <li key={idx}>
                                <strong>Item :</strong> {menuItem.nom}
                              </li>
                            ))
                          ) : (
                            <>
                              {item.menuDetails.selectedFormula && (
                                <li><strong>Formule :</strong> {item.menuDetails.selectedFormula}</li>
                              )}
                              {item.menuDetails.selectedEntrée && (
                                <li><strong>Entrée :</strong> {item.menuDetails.selectedEntrée.nom}</li>
                              )}
                              {item.menuDetails.selectedPlat && (
                                <li><strong>Plat :</strong> {item.menuDetails.selectedPlat.nom}</li>
                              )}
                              {item.menuDetails.selectedGarniture && (
                                <li><strong>Garniture :</strong> {item.menuDetails.selectedGarniture.nom}</li>
                              )}
                              {item.menuDetails.selectedDessert && (
                                <li><strong>Dessert :</strong> {item.menuDetails.selectedDessert.nom}</li>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </ul>
                  </Box>
                ))}
               

                <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>Plats à réaliser pour cette commande</Typography>
                <div className="plats-a-realiser">
                  {Object.entries(platsMap).map(([plat, quantity], index) => (
                    <Typography className="listPlatProd" key={index}>
                      <strong>{quantity} x {plat}</strong>
                    </Typography>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={dialogChangeTime.open} onClose={() => setDialogChangeTime({ open: false, cmdId: null })}>
        <DialogTitle>Changer l'heure de retrait</DialogTitle>
        <DialogContent>
          <TextField
            type="datetime-local"
            value={newPickupTime}
            onChange={(e) => setNewPickupTime(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDialogChangeTime({ open: false, cmdId: null })} sx={{ mr: 2 }}>
              Annuler
            </Button>
            <Button variant="contained" onClick={handleConfirmChangeTime}>
              Confirmer
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DashboardCommandes;